import React, { useState, useEffect, useRef } from "react";
import CustomCard from "../../components/CustomCard";
import CustomButton from "../../components/CustomButton";
import CustomDropdown from "../../components/CustomDropdown";
import Popup from "../../components/Popup";
import "../css/InformationAccount.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { getAgreements, createIdentityVerificationWithDocuments, createUserDocumentsMT5Account, fetchAndStoreServerPublicKey } from "../../utils/apiClient";
import AppConstants from "../../utils/appConstants";
import { UserDocumentSubType } from "../../utils/enums";
import rsaController from "../../utils/RSA-controller";
import "../css/identityVerification.css";

export default function IdentityVerification() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Agreements from API
  const [agreements, setAgreements] = useState([]);
  const [loadingAgreements, setLoadingAgreements] = useState(true);
  
  // Dynamic state for agreements: keyed by agreement ID
  // { [agreementId]: { checked: boolean, signatureData: string | null } }
  const [agreementStates, setAgreementStates] = useState({});
  
  // Dynamic refs for signature pads: keyed by agreement ID
  const signatureRefs = useRef({});
  
  const [loading, setLoading] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Document upload fields
  const [documentType, setDocumentType] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const [documentFrontImage, setDocumentFrontImage] = useState(null);
  const [documentFrontPreview, setDocumentFrontPreview] = useState(null);
  const [documentBackImage, setDocumentBackImage] = useState(null);
  const [documentBackPreview, setDocumentBackPreview] = useState(null);
  const [documentErrors, setDocumentErrors] = useState({});

  // Initialize RSA keys and fetch agreements from API
  useEffect(() => {
    const fetchAgreements = async () => {
      setLoadingAgreements(true);
      try {
        // Initialize RSA keys first (required for API calls)
        try {
          await rsaController.generateUserKeyPair();
          await fetchAndStoreServerPublicKey();
        } catch (rsaError) {
          // If RSA initialization fails, still try to continue (keys might already exist)
        }
        
        const response = await getAgreements();
        const responseCode = response?.data?.CODE;
        
        if (responseCode === AppConstants.responseOk) {
          const agreementsList = response?.data?.DATA || response?.agreements || [];
          setAgreements(agreementsList);
          
          // Initialize states for each agreement
          const initialStates = {};
          agreementsList.forEach(agreement => {
            if (agreement.ID) {
              initialStates[agreement.ID] = {
                checked: false,
                signatureData: null
              };
              // Initialize signature ref if not exists
              if (!signatureRefs.current[agreement.ID]) {
                signatureRefs.current[agreement.ID] = React.createRef();
              }
            }
          });
          setAgreementStates(initialStates);
          
          // Load saved data from sessionStorage if exists
          const savedData = sessionStorage.getItem('identity_verification_data');
          if (savedData) {
            try {
              const data = JSON.parse(savedData);
              if (data.agreementStates) {
                // Merge saved states with initial states
                const mergedStates = { ...initialStates };
                Object.keys(data.agreementStates).forEach(id => {
                  if (mergedStates[id]) {
                    mergedStates[id] = data.agreementStates[id];
                  }
                });
                setAgreementStates(mergedStates);
              }
            } catch (e) {
              sessionStorage.removeItem('identity_verification_data');
            }
          }
        } else {
          setErrorPopupMessage(t("identityVerification.errors.fetchAgreementsFailed") || "Failed to load agreements. Please try again.");
          setShowErrorPopup(true);
        }
      } catch (error) {
        const errorMessage = error?.message || error?.toString() || "Unknown error";
        setErrorPopupMessage(
          t("identityVerification.errors.fetchAgreementsFailed") || 
          `Failed to load agreements: ${errorMessage}. Please try again.`
        );
        setShowErrorPopup(true);
      } finally {
        setLoadingAgreements(false);
      }
    };

    fetchAgreements();
  }, [t]);

  // Auto-save form data
  useEffect(() => {
    if (agreements.length > 0) {
      const verificationData = {
        agreements: agreements.map(a => ({ ID: a.ID, KEY: a.KEY, NAME: a.NAME, AGREEMENT_NAME: a.AGREEMENT_NAME })),
        agreementStates,
        documentType
      };
      sessionStorage.setItem('identity_verification_data', JSON.stringify(verificationData));
    }
  }, [agreementStates, agreements, documentType]);

  // Load saved document data
  useEffect(() => {
    const savedData = sessionStorage.getItem('identity_verification_data');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        if (data.documentType !== null && data.documentType !== undefined) {
          setDocumentType(data.documentType);
        }
      } catch (e) {
        // Failed to parse
      }
    }
  }, []);

  // Restore signatures to canvas when agreementStates are loaded and refs are ready
  useEffect(() => {
    if (Object.keys(agreementStates).length > 0) {
      // Wait a bit for refs to be initialized
      const timer = setTimeout(() => {
        Object.keys(agreementStates).forEach(agreementId => {
          const state = agreementStates[agreementId];
          if (state && state.signatureData && signatureRefs.current[agreementId]?.current) {
            const sigRef = signatureRefs.current[agreementId].current;
            // Only restore if canvas is empty (to avoid overwriting user's current drawing)
            if (sigRef.isEmpty()) {
              const img = new Image();
              img.src = state.signatureData;
              img.onload = () => {
                const canvas = sigRef.getCanvas();
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              };
            }
          }
        });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [agreementStates]);

  // Check if document type requires 2 images (front and back)
  // Matches Flutter's isOneFace() logic: PASSPORT and VISA are one-face (front only)
  // All other identity documents require front + back
  const requiresTwoImages = () => {
    // Extract numeric value from documentType (handle both number and object)
    const docTypeValue = documentType !== null && documentType !== undefined
      ? (typeof documentType === 'number' 
          ? documentType 
          : (typeof documentType === 'object' && documentType?.value !== undefined 
              ? documentType.value 
              : parseInt(documentType, 10)))
      : null;
    
    if (docTypeValue === null) return false;
    
    // Flutter's isOneFace() returns true for PASSPORT and VISA (front only)
    // Returns false for all others (front + back required)
    if (docTypeValue === UserDocumentSubType.PASSPORT || 
        docTypeValue === UserDocumentSubType.VISA) {
      return false; // One image only (front)
    }
    
    // All other identity documents require two images (front + back)
    // ID_CARD (1), CARD_OF_RESIDENCE (3), PALESTINIAN_CARD (4), DRIVE_LICENCE (6)
    return true;
  };
  
  // Check if document type is an address document (uses ADDRESS_IMAGE field)
  // Matches Flutter: BANK_STATEMENT, TAX_STATEMENT, UTILITY_BILL, CERTIFICATE_OF_RESIDENCE
  const isAddressDocument = () => {
    const docTypeValue = documentType !== null && documentType !== undefined
      ? (typeof documentType === 'number' 
          ? documentType 
          : (typeof documentType === 'object' && documentType?.value !== undefined 
              ? documentType.value 
              : parseInt(documentType, 10)))
      : null;
    
    if (docTypeValue === null) return false;
    
    // Address documents use ADDRESS_IMAGE field (single image)
    return docTypeValue === UserDocumentSubType.BANK_STATEMENT ||
           docTypeValue === UserDocumentSubType.TAX_STATEMENT ||
           docTypeValue === UserDocumentSubType.UTILITY_BILL ||
           docTypeValue === UserDocumentSubType.CERTIFICATE_OF_RESIDENCE;
  };

  // Handle document type change
  const handleDocumentTypeChange = (selected) => {
    setDocumentType(selected?.value || null);
    // Clear images when document type changes
    setDocumentFrontImage(null);
    setDocumentFrontPreview(null);
    setDocumentBackImage(null);
    setDocumentBackPreview(null);
    // Clear all document errors (including documentType error)
    setDocumentErrors({});
  };

  // Handle file upload
  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'selfie') {
        setSelfieImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelfiePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (type === 'front') {
        setDocumentFrontImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setDocumentFrontPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (type === 'back') {
        setDocumentBackImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setDocumentBackPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
      // Clear error for this field
      if (documentErrors[type]) {
        setDocumentErrors({ ...documentErrors, [type]: null });
      }
    }
  };

  // Remove uploaded image
  const handleRemoveImage = (type) => {
    if (type === 'selfie') {
      setSelfieImage(null);
      setSelfiePreview(null);
      const input = document.getElementById('selfieInput');
      if (input) input.value = "";
    } else if (type === 'front') {
      setDocumentFrontImage(null);
      setDocumentFrontPreview(null);
      const input = document.getElementById('documentFrontInput');
      if (input) input.value = "";
    } else if (type === 'back') {
      setDocumentBackImage(null);
      setDocumentBackPreview(null);
      const input = document.getElementById('documentBackInput');
      if (input) input.value = "";
    }
  };

  const handleAgreementChange = (agreementId) => (e) => {
    const isChecked = e.target.checked;
    setAgreementStates(prev => ({
      ...prev,
      [agreementId]: {
        ...prev[agreementId],
        checked: isChecked,
        signatureData: isChecked ? prev[agreementId]?.signatureData || null : null
      }
    }));
    
    // Clear signature if unchecked
    if (!isChecked && signatureRefs.current[agreementId]?.current) {
      signatureRefs.current[agreementId].current.clear();
      setAgreementStates(prev => ({
        ...prev,
        [agreementId]: {
          ...prev[agreementId],
          signatureData: null
        }
      }));
    }
  };

  const handleClearSignature = (agreementId) => {
    if (signatureRefs.current[agreementId]?.current) {
      signatureRefs.current[agreementId].current.clear();
      setAgreementStates(prev => ({
        ...prev,
        [agreementId]: {
          ...prev[agreementId],
          signatureData: null
        }
      }));
    }
  };

  const handleSaveSignature = (agreementId) => {
    const signatureRef = signatureRefs.current[agreementId]?.current;
    if (signatureRef && !signatureRef.isEmpty()) {
      const dataURL = signatureRef.toDataURL();
      setAgreementStates(prev => {
        const newState = {
        ...prev,
        [agreementId]: {
          ...prev[agreementId],
            checked: prev[agreementId]?.checked || false,
          signatureData: dataURL
        }
        };
        return newState;
      });
    } else {
      setErrorPopupMessage(t("identityVerification.errors.signatureRequired") || "Please provide your signature.");
      setShowErrorPopup(true);
    }
  };

  const handleNext = async () => {
    // Validate all agreements are checked
    const allChecked = agreements.every(agreement => {
      const state = agreementStates[agreement.ID];
      return state && state.checked === true;
    });

    if (!allChecked) {
      const uncheckedAgreements = agreements.filter(agreement => {
        const state = agreementStates[agreement.ID];
        return !state || !state.checked;
      });
      setErrorPopupMessage(t("identityVerification.errors.allAgreementsRequired") || "Please accept all agreements and terms to continue.");
      setShowErrorPopup(true);
      return;
    }

    // Validate all signatures are provided for checked agreements
    // First, try to auto-save any signatures that are drawn but not saved
    for (const agreement of agreements) {
      const agreementId = agreement.ID;
      const state = agreementStates[agreementId];
      if (state && state.checked) {
        const signatureRef = signatureRefs.current[agreementId]?.current;
        // If signature is checked but no signatureData, check if canvas has content
        if (!state.signatureData && signatureRef && !signatureRef.isEmpty()) {
          const dataURL = signatureRef.toDataURL();
          setAgreementStates(prev => ({
            ...prev,
            [agreementId]: {
              ...prev[agreementId],
              checked: true,
              signatureData: dataURL
            }
          }));
        }
      }
    }

    // Wait a moment for state to update, then validate
    await new Promise(resolve => setTimeout(resolve, 100));

    // Now validate signatures
    for (const agreement of agreements) {
      const state = agreementStates[agreement.ID];
      if (state && state.checked) {
        // Check if signature data exists
        if (!state.signatureData) {
          // Double-check the canvas
          const signatureRef = signatureRefs.current[agreement.ID]?.current;
          if (signatureRef && !signatureRef.isEmpty()) {
            // Canvas has content but wasn't saved - auto-save it now
            const dataURL = signatureRef.toDataURL();
            setAgreementStates(prev => ({
              ...prev,
              [agreement.ID]: {
                ...prev[agreement.ID],
                checked: true,
                signatureData: dataURL
              }
            }));
            // Wait for state update
            await new Promise(resolve => setTimeout(resolve, 50));
            continue; // Skip error for this one, we just saved it
          }
          
          const agreementName = agreement.AGREEMENT_NAME || agreement.NAME || agreement.KEY || "Agreement";
          setErrorPopupMessage(t("identityVerification.errors.signatureRequired") || `Please provide your signature for ${agreementName}.`);
          setShowErrorPopup(true);
          return;
        }
      }
    }

    // Validate all document-related fields and show red borders on missing ones
    const newErrors = {};

    // Validate document type
    if (!documentType) {
      newErrors.documentType = true;
    }

    // Validate selfie image
    if (!selfieImage) {
      newErrors.selfie = t("identityVerification.errors.selfieRequired") || "Please upload your selfie image.";
    }

    // Validate document images based on type
    const needsTwoImages = requiresTwoImages();
    
    if (!documentFrontImage) {
      newErrors.front = t("identityVerification.errors.documentFrontRequired") || "Please upload the front of your document.";
    }
    
    if (needsTwoImages && !documentBackImage) {
      newErrors.back = t("identityVerification.errors.documentBackRequired") || "Please upload the back of your document.";
    }

    if (Object.keys(newErrors).length > 0) {
      setDocumentErrors(newErrors);
      // Build appropriate error message
      let errorMsg;
      if (!documentType) {
        errorMsg = t("identityVerification.errors.documentTypeRequired") || "Please select a document type.";
      } else if (newErrors.selfie && (newErrors.front || newErrors.back)) {
        errorMsg = needsTwoImages 
          ? (t("identityVerification.errors.bothDocumentsRequired") || "Please upload all required images.")
          : (t("identityVerification.errors.documentRequired") || "Please upload your document.");
      } else if (newErrors.selfie) {
        errorMsg = t("identityVerification.errors.selfieRequired") || "Please upload your selfie image.";
      } else {
        errorMsg = needsTwoImages 
          ? (t("identityVerification.errors.bothDocumentsRequired") || "Please upload both front and back of your document.")
          : (t("identityVerification.errors.documentRequired") || "Please upload your document.");
      }
      setErrorPopupMessage(errorMsg);
      setShowErrorPopup(true);
      return;
    }

    setLoading(true);

    try {
      // Get email
      let email = localStorage.getItem('registrationEmail') || localStorage.getItem('userEmail');
      
      if (!email) {
        setErrorPopupMessage(t("identityVerification.errors.emailRequired") || "Email is required. Please complete registration first.");
        setShowErrorPopup(true);
        setLoading(false);
        return;
      }

      // Get user_id if available (from previous steps or login)
      // Try multiple possible storage keys
      let userId = localStorage.getItem('userId') || 
                   localStorage.getItem('user_id') || 
                   localStorage.getItem('USER_ID') ||
                   sessionStorage.getItem('userId') ||
                   sessionStorage.getItem('user_id') ||
                   sessionStorage.getItem('USER_ID') ||
                   null;
      
      // If userId is a string, try to parse it as integer
      if (userId && typeof userId === 'string') {
        const parsedUserId = parseInt(userId, 10);
        userId = isNaN(parsedUserId) ? null : parsedUserId;
      }
      
      // Prepare agreement IDs (only checked agreements)
      const checkedAgreementIds = agreements
        .filter(agreement => agreementStates[agreement.ID]?.checked)
        .map((agreement) => ({
          key: `AGREEMENT_${agreement.ID}_ID`,
          value: String(agreement.ID)
        }));

      // Build agreement IDs object using Flutter format: AGREEMENT_{ID}_ID = "ID"
      const agreementIds = {};
      checkedAgreementIds.forEach(({ key, value }) => {
        agreementIds[key] = value;
      });

      // Helper function to convert dataURL to File (matches Flutter's saveSignatureToFile)
      const dataURLtoFile = (dataurl, filename) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      };

      // Prepare ALL image files in one object (matches Flutter's imageData map)
      const imageFiles = {};
      
      // Add selfie image (matches Flutter: if (_selfieImage != null) imageData['SELFIE_IMAGE'] = _selfieImage!;)
      if (selfieImage) {
        imageFiles['SELFIE_IMAGE'] = selfieImage;
      }
      
      // Add document images (matches Flutter structure exactly)
      // Extract document type value to ensure correct comparison
      const docTypeValue = documentType !== null && documentType !== undefined
        ? (typeof documentType === 'number' 
            ? documentType 
            : (typeof documentType === 'object' && documentType?.value !== undefined 
                ? documentType.value 
                : parseInt(documentType, 10)))
        : null;
      
      // Flutter logic:
      // - Identity documents: ID_CARD, PASSPORT, CARD_OF_RESIDENCE, PALESTINIAN_CARD, DRIVE_LICENCE, VISA
      //   → Use FRONT_ID_IMAGE (and BACK_ID_IMAGE if not PASSPORT/VISA)
      // - Address documents: BANK_STATEMENT, TAX_STATEMENT, UTILITY_BILL, CERTIFICATE_OF_RESIDENCE
      //   → Use ADDRESS_IMAGE (single image)
      
      if (isAddressDocument()) {
        // Address documents (PROOF_OF_ADDRESS) - always use ADDRESS_IMAGE (single image)
        if (documentFrontImage) {
          imageFiles['ADDRESS_IMAGE'] = documentFrontImage;
        } else {
        }
      } else if (needsTwoImages) {
        // Identity documents requiring two images (ID_CARD, CARD_OF_RESIDENCE, PALESTINIAN_CARD, DRIVE_LICENCE)
        if (documentFrontImage) {
          imageFiles['FRONT_ID_IMAGE'] = documentFrontImage;
        }
        if (documentBackImage) {
          imageFiles['BACK_ID_IMAGE'] = documentBackImage;
        }
      } else {
        // Identity documents requiring one image only (PASSPORT, VISA) - use FRONT_ID_IMAGE
        // Note: Flutter uses FRONT_ID_IMAGE for PASSPORT/VISA, not ADDRESS_IMAGE
        if (documentFrontImage) {
          imageFiles['FRONT_ID_IMAGE'] = documentFrontImage;
        }
      }

      // Add signature images
      // 1) AGREEMENT_{ID}_SIGNATURE for every checked agreement (matches Flutter's dynamic map)
      // 2) Legacy SIGNATURE_* fields for the 4 main agreements (client_agreement, risk_disclosure, etc.)
      
      // Map each agreement to its signature field name (exact Flutter logic)
      const signatureMapping = {
        'client_agreement': 'SIGNATURE_AGREEMENT_IMAGE',
        'risk_disclosure': 'SIGNATURE_DISCLOSURE_IMAGE',
        'privacy_policy': 'SIGNATURE_PRIVACY_IMAGE',
        'terms_of_service': 'SIGNATURE_SERVICE_IMAGE',
        'term_of_service': 'SIGNATURE_SERVICE_IMAGE', // Alternative spelling
      };
      
      for (const agreement of agreements) {
        const state = agreementStates[agreement.ID];
        if (state && state.checked && state.signatureData) {
          try {
            // Get agreement key (matches Flutter's filename pattern)
            const agreementKey = (agreement.KEY || agreement.AGREEMENT_NAME || agreement.NAME || `agreement_${agreement.ID}`).toLowerCase().trim();
            const signatureFileName = `${agreementKey}_signature.png`;
            const signatureFile = dataURLtoFile(state.signatureData, signatureFileName);

            // Always add dynamic AGREEMENT_{ID}_SIGNATURE field (Flutter's imageData['AGREEMENT_${agreementId}_SIGNATURE'])
            const agreementIdStr = String(agreement.ID);
            const dynamicSignatureKey = `AGREEMENT_${agreementIdStr}_SIGNATURE`;
            imageFiles[dynamicSignatureKey] = signatureFile;
            // Also add legacy SIGNATURE_* field when applicable (exact Flutter mapping)
            let signatureFieldName = null;
            
            // Try exact match first
            if (signatureMapping[agreementKey]) {
              signatureFieldName = signatureMapping[agreementKey];
            } else {
              // Try partial match (in case key has variations)
              for (const [key, fieldName] of Object.entries(signatureMapping)) {
                if (agreementKey.includes(key) || key.includes(agreementKey)) {
                  signatureFieldName = fieldName;
                  break;
                }
              }
            }
            
            // Only add legacy signature if it matches one of the 4 hardcoded field names
            if (signatureFieldName) {
              // If field already exists, log warning (Flutter would overwrite)
              if (imageFiles[signatureFieldName]) {
                // Field already exists, will be overwritten
              }
              
              imageFiles[signatureFieldName] = signatureFile;
            } else {
              // No legacy mapping, but dynamic AGREEMENT_{ID}_SIGNATURE was still added above
              // Agreement has signature but doesn't match Flutter's 4 legacy signature field names
            }
          } catch (error) {
          }
        }
      }
      
      // Log summary (matches Flutter's behavior)
      const signatureFieldsSent = Object.keys(imageFiles).filter(key => key.startsWith('SIGNATURE_'));
      // Send ALL documents in ONE request (matches Flutter's postDataAndImage approach)
      // Ensure documentType is passed as a number, not an object
      const documentTypeToSend = documentType !== null && documentType !== undefined
        ? (typeof documentType === 'number' 
            ? documentType 
            : (typeof documentType === 'object' && documentType?.value !== undefined 
                ? documentType.value 
                : parseInt(documentType, 10)))
        : null;
      
      const response = await createIdentityVerificationWithDocuments(
        email,
        documentTypeToSend, // Pass extracted numeric value
        null, // identificationNumber - not required
        imageFiles, // Send all images in one request
        agreementIds,
        { userId } // Pass user_id if available
      );

      const responseCode = response?.decryptedData?.CODE || response?.data?.CODE;

      if (responseCode === AppConstants.responseOk) {
        // Success
      sessionStorage.setItem('identity_verification_completed', 'true');
      sessionStorage.removeItem('identity_verification_data');
        sessionStorage.removeItem('urban_info_data');

      setShowSuccessPopup(true);
      
      // Navigate to home page after a short delay
      setTimeout(() => {
        navigate("/home");
      }, 2000);
      } else {
        // Error
        let errorMessage = response?.decryptedData?.MESSAGE || 
                          response?.decryptedData?.message ||
                          response?.data?.MESSAGE ||
                          response?.data?.message ||
                          t("identityVerification.errors.submitFailed") || 
                          "Failed to submit identity verification. Please try again.";
        
        setErrorPopupMessage(errorMessage);
        setShowErrorPopup(true);
      }
    } catch (error) {
      let errorMessage = t("identityVerification.errors.submitFailed") || "An error occurred while saving identity verification. Please try again.";
      
      if (error.response?.data) {
        try {
          const errorData = error.response.data;
          errorMessage = errorData?.MESSAGE || 
                        errorData?.message || 
                        errorData?.error || 
                        errorData?.ERROR ||
                        errorMessage;
        } catch (e) {
          // Failed to parse error data
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrorPopupMessage(errorMessage);
      setShowErrorPopup(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <CustomCard>
        <div className="signup-content">
          <h1 className="signup-title">{t("identityVerification.title") || "Identity Verification"}</h1>

          {/* Progress Indicator - Steps 1, 2, and 3 active */}
          <div className="progress-indicator">
            <div className="progress-circle active">
              {t("identityVerification.progress.step1") || t("userRegulation.progress.step1") || "01"}
            </div>
            <div className="progress-line progress-line-active" />
            <div className="progress-circle active">
              {t("identityVerification.progress.step2") || t("userRegulation.progress.step2") || "02"}
            </div>
            <div className="progress-line progress-line-active" />
            <div className="progress-circle active">
              {t("identityVerification.progress.step3") || t("userRegulation.progress.step3") || "03"}
            </div>
          </div>

          {/* Form */}
          <div className="signup-form">
            {/* Agreements List */}
            <div className="agreements-list">
              {loadingAgreements ? (
                <div className="loading-agreements">
                  {t("common.loading") || "Loading agreements..."}
                </div>
              ) : agreements.length === 0 ? (
                <div className="no-agreements">
                  {t("identityVerification.noAgreements") || "No agreements available."}
                </div>
              ) : (
                agreements.map((agreement) => {
                  const agreementId = agreement.ID;
                  const agreementState = agreementStates[agreementId] || { checked: false, signatureData: null };
                  const signatureRef = signatureRefs.current[agreementId];
                  const agreementName = agreement.AGREEMENT_NAME || agreement.NAME || agreement.KEY || "Agreement";
                  
                  // Initialize ref if not exists
                  if (!signatureRef) {
                    signatureRefs.current[agreementId] = React.createRef();
                  }
                  
                  return (
                    <div key={agreementId} className="form-group agreement-item">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={agreementState.checked || false}
                          onChange={handleAgreementChange(agreementId)}
                          className="checkbox-input"
                        />
                        <span 
                          className="checkbox-text agreement-title"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Navigate to agreement page with key and name
                            navigate(`/agreement?key=${encodeURIComponent(agreement.KEY || agreement.ID)}&name=${encodeURIComponent(agreementName)}`);
                          }}
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          title={t("identityVerification.viewAgreement") || "Click to view full agreement"}
                        >
                          {agreementName}
                        </span>
                      </label>
                      {agreementState.checked && (
                        <div className="signature-group">
                          <div className="signature-pad-container">
                            <SignatureCanvas
                              ref={signatureRefs.current[agreementId]}
                              canvasProps={{
                                className: "signature-canvas",
                                width: 600,
                                height: 200,
                              }}
                              backgroundColor="#ffffff"
                              penColor="#141414"
                              onEnd={() => {
                                // Auto-save signature when user finishes drawing
                                const sigRef = signatureRefs.current[agreementId]?.current;
                                if (sigRef && !sigRef.isEmpty()) {
                                  const dataURL = sigRef.toDataURL();
                                  setAgreementStates(prev => ({
                                    ...prev,
                                    [agreementId]: {
                                      ...prev[agreementId],
                                      checked: true,
                                      signatureData: dataURL
                                    }
                                  }));
                                }
                              }}
                            />
                            <div className="signature-actions">
                              <button
                                type="button"
                                onClick={() => handleClearSignature(agreementId)}
                                className="signature-clear-btn"
                                title={t("identityVerification.signature.clear") || "Clear"}
                              >
                                <i className="fa-solid fa-rotate-left"></i>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSaveSignature(agreementId)}
                                className="signature-save-btn"
                                title={t("identityVerification.signature.save") || "Save"}
                              >
                                <i className="fa-solid fa-check"></i>
                              </button>
                            </div>
                            {agreementState.signatureData && (
                              <div className="signature-saved-indicator">
                                <i className="fa-solid fa-circle-check"></i>
                                <span>{t("identityVerification.signature.saved") || "Signature saved"}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Document Upload Section */}
            <div className="document-upload-section">
              <h3 className="section-title">{t("identityVerification.documentSection.title") || "Identity Document"}</h3>
              
              {/* Selfie Image */}
              <div className="form-group">
                <label>{t("identityVerification.selfie.label") || "Selfie Image"}</label>
                <div className={`file-upload-wrapper ${documentErrors.selfie ? "input-error" : ""}`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'selfie')}
                    className="file-input"
                    id="selfieInput"
                  />
                  <label htmlFor="selfieInput" className="file-upload-area">
                    {selfiePreview ? (
                      <div className="image-preview-container">
                        <img 
                          src={selfiePreview} 
                          alt={t("identityVerification.selfie.preview") || "Selfie preview"} 
                          className="uploaded-image"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveImage('selfie');
                          }}
                          className="remove-image-btn"
                          aria-label="Remove image"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                        <button
                          type="button"
                          className="upload-button-circle change-image-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            document.getElementById("selfieInput")?.click();
                          }}
                          aria-label="Change image"
                        >
                          <i className="fa-solid fa-camera"></i>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="camera-icon-placeholder">
                          <i className="fa-solid fa-camera"></i>
                        </div>
                        <button
                          type="button"
                          className="upload-button-circle"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("selfieInput")?.click();
                          }}
                          aria-label="Upload image"
                        >
                          <i className="fa-solid fa-camera"></i>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                        <span className="document-image-text">
                          {t("identityVerification.selfie.label") || "Selfie Image"}
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Document Type */}
              <div className="form-group">
                <label>{t("identityVerification.documentType.label") || "Document Type"}</label>
                <div className="input-wrapper">
                  <div className="dropdown-wrapper">
                    <CustomDropdown
                      options={[
                        // Identity documents (PROOF_OF_IDENTITY) - matches Flutter's createDocumentDropdownItems()
                        { value: UserDocumentSubType.ID_CARD, label: t("identityVerification.documentType.options.idCard") || "ID Card" },
                        { value: UserDocumentSubType.PASSPORT, label: t("identityVerification.documentType.options.passport") || "Passport" },
                        { value: UserDocumentSubType.CARD_OF_RESIDENCE, label: t("identityVerification.documentType.options.cardOfResidence") || "Card of Residence" },
                        { value: UserDocumentSubType.PALESTINIAN_CARD, label: t("identityVerification.documentType.options.palestinianCard") || "Palestinian Card" },
                        { value: UserDocumentSubType.VISA, label: t("identityVerification.documentType.options.visa") || "Visa" },
                        // Address documents (PROOF_OF_ADDRESS) - matches Flutter's addressDropdownItems
                        { value: UserDocumentSubType.BANK_STATEMENT, label: t("identityVerification.documentType.options.bankStatement") || "Bank Statement" },
                        { value: UserDocumentSubType.TAX_STATEMENT, label: t("identityVerification.documentType.options.taxStatement") || "Tax Statement" },
                      ]}
                      value={
                        documentType !== null && documentType !== undefined
                          ? (() => {
                              const option = [
                                // Identity documents
                                { value: UserDocumentSubType.ID_CARD, label: t("identityVerification.documentType.options.idCard") || "ID Card" },
                                { value: UserDocumentSubType.PASSPORT, label: t("identityVerification.documentType.options.passport") || "Passport" },
                                { value: UserDocumentSubType.CARD_OF_RESIDENCE, label: t("identityVerification.documentType.options.cardOfResidence") || "Card of Residence" },
                                { value: UserDocumentSubType.PALESTINIAN_CARD, label: t("identityVerification.documentType.options.palestinianCard") || "Palestinian Card" },
                                { value: UserDocumentSubType.VISA, label: t("identityVerification.documentType.options.visa") || "Visa" },
                                // Address documents
                                { value: UserDocumentSubType.BANK_STATEMENT, label: t("identityVerification.documentType.options.bankStatement") || "Bank Statement" },
                                { value: UserDocumentSubType.TAX_STATEMENT, label: t("identityVerification.documentType.options.taxStatement") || "Tax Statement" },
                              ].find(opt => opt.value === documentType);
                              return option || { value: documentType, label: `Document Type ${documentType}` };
                            })()
                          : null
                      }
                      onChange={handleDocumentTypeChange}
                      placeholder={t("identityVerification.documentType.placeholder") || "Select document type"}
                      className={documentErrors.documentType ? "dropdown-error" : ""}
                    />
                  </div>
                </div>
              </div>

              {/* Document Front Image */}
              <div className="form-group">
                <label>
                  {requiresTwoImages() 
                    ? (t("identityVerification.documentFront.label") || "ID Front")
                    : (t("identityVerification.documentImage.label") || "Document Image")}
                </label>
                <div className={`file-upload-wrapper ${documentErrors.front ? "input-error" : ""}`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'front')}
                    className="file-input"
                    id="documentFrontInput"
                  />
                  <label htmlFor="documentFrontInput" className="file-upload-area">
                    {documentFrontPreview ? (
                      <div className="image-preview-container">
                        <img 
                          src={documentFrontPreview} 
                          alt={t("identityVerification.documentFront.preview") || "Document preview"} 
                          className="uploaded-image"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveImage('front');
                          }}
                          className="remove-image-btn"
                          aria-label="Remove image"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                        <button
                          type="button"
                          className="upload-button-circle change-image-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            document.getElementById("documentFrontInput")?.click();
                          }}
                          aria-label="Change image"
                        >
                          <i className="fa-solid fa-camera"></i>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="camera-icon-placeholder">
                          <i className="fa-solid fa-camera"></i>
                        </div>
                        <button
                          type="button"
                          className="upload-button-circle"
                          onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("documentFrontInput")?.click();
                          }}
                          aria-label="Upload image"
                        >
                          <i className="fa-solid fa-camera"></i>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                        <span className="document-image-text">
                          {requiresTwoImages() 
                            ? (t("identityVerification.documentFront.label") || "ID Front")
                            : (t("identityVerification.documentImage.label") || "Document Image")}
                        </span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              {/* Document Back Image (only for ID Card and Palestinian Card) */}
              {requiresTwoImages() && (
                <div className="form-group">
                  <label>{t("identityVerification.documentBack.label") || "ID Back"}</label>
                  <div className={`file-upload-wrapper ${documentErrors.back ? "input-error" : ""}`}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e, 'back')}
                      className="file-input"
                      id="documentBackInput"
                    />
                    <label htmlFor="documentBackInput" className="file-upload-area">
                      {documentBackPreview ? (
                        <div className="image-preview-container">
                          <img 
                            src={documentBackPreview} 
                            alt={t("identityVerification.documentBack.preview") || "Document back preview"} 
                            className="uploaded-image"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRemoveImage('back');
                            }}
                            className="remove-image-btn"
                            aria-label="Remove image"
                          >
                            <i className="fa-solid fa-times"></i>
                          </button>
                          <button
                            type="button"
                            className="upload-button-circle change-image-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              document.getElementById("documentBackInput")?.click();
                            }}
                            aria-label="Change image"
                          >
                            <i className="fa-solid fa-camera"></i>
                            <i className="fa-solid fa-plus"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="camera-icon-placeholder">
                            <i className="fa-solid fa-camera"></i>
                          </div>
                          <button
                            type="button"
                            className="upload-button-circle"
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById("documentBackInput")?.click();
                            }}
                            aria-label="Upload image"
                          >
                            <i className="fa-solid fa-camera"></i>
                            <i className="fa-solid fa-plus"></i>
                          </button>
                          <span className="document-image-text">
                            {t("identityVerification.documentBack.label") || "ID Back"}
                          </span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}
            </div>

            <CustomButton 
              className="next-button" 
              onClick={handleNext}
              disabled={loading}
            >
              {loading ? (t("common.loading") || "Loading...") : (t("identityVerification.nextButton") || "Finish")}
            </CustomButton>
          </div>
        </div>
      </CustomCard>

      <Popup
        isOpen={showErrorPopup}
        onClose={() => setShowErrorPopup(false)}
        title={t("common.error") || "Error"}
        message={errorPopupMessage}
        buttonText={t("common.ok") || "OK"}
        icon="fa-circle-exclamation"
      />

      <Popup
        isOpen={showSuccessPopup}
        onClose={() => {
          setShowSuccessPopup(false);
          navigate("/home");
        }}
        title={t("common.success") || "Success"}
        message={t("identityVerification.successMessage") || "Identity verification completed successfully!"}
        buttonText={t("common.ok") || "OK"}
        icon="fa-circle-check"
      />
    </div>
  );
}